// URL <-> view mapping for the app's single-view navigation.
//
// The app renders one "view" at a time, driven by the useCurrentView store
// (App.tsx switches on currentView). Rather than migrate to react-router, this
// module is the single source of truth for translating between a view name and
// a URL path, so navigation can be reflected in the address bar — giving real
// per-view URLs, working back/forward buttons, and shareable deep-links.

export const VIEWS = [
  'dashboard', // "Home" — the new daily command center (replaced the old Today page)
  'inbox',     // capture → clarify → categorize → prioritize → route
  'roadmap',   // the progression system: Growth Journey · System Mastery · Domain Progression
  'current',
  'habits',
  'routines',
  'tasks',
  'status',
  'wallet',   // its own page since 2026-08-12 — was a Status tab, but the rail
              // widget's only destination WAS that tab, so it was never reachable
              // any other way. See StatusView + moduleReveal.
  'settings',
  'domain',
  // ⛔ 'domains' (the Domains page) RETIRED 2026-10-03 — see `pathToView` / view-storage v3.
  'projects',
  'quests',
  'abilities',
  'action',
  'discover', // was 'social' — Connect, now also hosting Guilds + Marketplace
  'guilds',
  'profile',
  'journal',
  'home',
  'calendar',
  'templates',
  'programs',
  'marketplace',
  'assistant', // full-page AI assistant (the panel's expanded surface)
] as const;

export type View = (typeof VIEWS)[number];

const VIEW_SET = new Set<string>(VIEWS);

export function isView(value: string): value is View {
  return VIEW_SET.has(value);
}

/**
 * Map a view (+ optional entity id) to a URL path.
 * The domain and profile views carry their selected entity as a path segment
 * so they can be deep-linked, e.g. /domain/personal or /profile/<userId>.
 */
export function viewToPath(view: View, entityId?: string | null): string {
  if (view === 'domain' && entityId) {
    return `/domain/${encodeURIComponent(entityId)}`;
  }
  if (view === 'profile' && entityId) {
    return `/profile/${encodeURIComponent(entityId)}`;
  }
  return `/${view}`;
}

/**
 * Parse a URL path into a view (+ domain id when present).
 * Returns null for the root path OR any unrecognized path (e.g. /assess/:token,
 * which App.tsx handles separately) — callers then leave the URL untouched and
 * fall back to the persisted/default view.
 */
export function pathToView(pathname: string): { view: View; domainId?: string; profileId?: string; roadmapTrack?: 'domains' } | null {
  const clean = pathname.replace(/^\/+|\/+$/g, '');
  if (!clean) return null;

  const [segment, sub] = clean.split('/');

  if (segment === 'domain') {
    return sub ? { view: 'domain', domainId: decodeURIComponent(sub) } : { view: 'domain' };
  }

  if (segment === 'profile') {
    return sub ? { view: 'profile', profileId: decodeURIComponent(sub) } : { view: 'profile' };
  }

  // ⚠️ `/social` shipped as a real, shareable URL before Connect became
  // Discover. Old links, bookmarks and any pasted URL must keep working, so the
  // legacy segment resolves to the new view rather than 404-ing to the default.
  if (segment === 'social') return { view: 'discover' };

  // ⚠️ `/domains` was the Domains page (retired 2026-10-03). Its content lives in
  // Roadmap → Domain Progression, so an old link lands THERE, not on a blank view.
  if (segment === 'domains') return { view: 'roadmap', roadmapTrack: 'domains' };

  return isView(segment) ? { view: segment } : null;
}

/**
 * The view a signed-in person should actually be on, given what reveal allows.
 *
 * ⚠️⚠️ THE RAIL HIDING A DESTINATION DOES NOT STOP THE APP RENDERING IT.
 * `currentView` is restored from `view-storage` and from the URL, and neither
 * knows anything about reveal — so on 2026-09-01 a signed-in user landed on
 * Connect with a rail beside it offering only Home. The nav filtered itself
 * correctly; the renderer never asked the same question.
 *
 * ⚠️ Only the rail's OWN destinations are checked. `isRevealed` fails OPEN on a
 * surface it does not know, so asking about `nav:settings` — which has no reveal
 * step — would return `true` and quietly do nothing, which looks exactly like
 * working. Passing the list in keeps this honest and keeps the module free of a
 * circular import back to `Navigation`.
 *
 * ⭐ The fallback is the ROADMAP (owner, 2026-09-29): Home is gated until step 4
 * now, so it can no longer be the safe destination. `nav:roadmap` is
 * deliberately never revealable — the Growth Module lives there — so returning
 * it unchecked cannot bounce.
 */
/** Where a new user lands, and where a hidden page sends you. Never gated. */
export const LANDING_VIEW: View = 'roadmap';

export function resolveLandingView(
  current: View,
  primaryIds: readonly View[],
  revealed: (surface: string) => boolean,
): View {
  if (current === LANDING_VIEW) return current;
  if (!primaryIds.includes(current)) return current;
  return revealed(`nav:${current}`) ? current : LANDING_VIEW;
}
